import React, { FunctionComponent, PropsWithChildren, useEffect } from "react";
import Helmet from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import ReactGA from "react-ga";

interface IMetaAsName {
    name: string;
    content?: string;
}

interface IMetaAsProperty {
    property: string;
    content?: string;
}

type Meta = (IMetaAsName | IMetaAsProperty)[];

interface IProps {
    description?: string;
    lang: string;
    meta: Meta;
    title: string;
}

export const SEO: FunctionComponent<IProps> = ({ description, lang, meta, title }: PropsWithChildren<IProps>) => {
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                        author
                    }
                }
            }
        `,
    );

    const metaDescription = `${description || site.siteMetadata.description}`;

    const fullMeta: Meta = [
        {
            name: `description`,
            content: metaDescription,
        },
        {
            property: `og:title`,
            content: title,
        },
        {
            property: `og:description`,
            content: metaDescription,
        },
        {
            property: `og:type`,
            content: `website`,
        },
        {
            name: `twitter:card`,
            content: `summary`,
        },
        {
            name: `twitter:creator`,
            content: `${site.siteMetadata.author}`,
        },
        {
            name: `twitter:title`,
            content: title,
        },
        {
            name: `twitter:description`,
            content: metaDescription,
        },
    ];

    useEffect(() => {
        ReactGA.initialize(`${process.env.GATSBY_GOOGLE_ANALYTICS_ID}`);
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const mergedMeta = fullMeta.concat(meta);

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const favicon = require("../images/favicon.ico");

    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
            title={title}
            titleTemplate={`${site.siteMetadata.title} | %s`}
            meta={mergedMeta}
            link={[
                {
                    rel: "shortcut icon",
                    href: favicon,
                    type: "image/ico",
                },
                {
                    href: "https://fonts.googleapis.com/css?family=Luckiest+Guy&display=swap",
                    rel: "stylesheet",
                },
            ]}
        />
    );
};

SEO.defaultProps = {
    lang: `en`,
    meta: [],
    description: ``,
};
